import React from 'react';
import classNames from 'classnames';

import Spinner from '@cobalt/react-spinner';

import TimelineItem from './TimelineItem';
import { TimelineProps, TimelineItemProps } from './interface';

import './styles.scss';

const cls = 'timeline';

interface TimelineType extends React.FC<TimelineProps> {
  Item: React.FC<TimelineItemProps>;
}

const Timeline: TimelineType = (props) => {
  const {
    prefixCls,
    pending = null,
    pendingDot,
    children,
    className,
    reverse = false,
    mode = '' as TimelineProps['mode'],
    ...restProps
  } = props;
  const pendingNode = typeof pending === 'boolean' ? null : pending;

  const pendingItem = pending ? (
    <TimelineItem pending={!!pending} dot={pendingDot || <Spinner size="micro" />}>
      {pendingNode}
    </TimelineItem>
  ) : null;

  const timeLineItems = React.Children.toArray(children);
  timeLineItems.push(pendingItem as any);
  if (reverse) {
    timeLineItems.reverse();
  }

  const componentCls = `${prefixCls}-${cls}`;

  const getPositionCls = (ele: React.ReactElement<any>, idx: number) => {
    if (mode === 'alternate') {
      if (ele.props.position === 'right') return `${componentCls}-item-right`;
      if (ele.props.position === 'left') return `${componentCls}-item-left`;
      return idx % 2 === 0 ? `${componentCls}-item-left` : `${componentCls}-item-right`;
    }
    if (mode === 'left') return `${componentCls}-item-left`;
    if (mode === 'right') return `${componentCls}-item-right`;
    if (ele.props.position === 'right') return `${componentCls}-item-right`;
    return '';
  };

  const truthyItems = timeLineItems.filter((item) => !!item) as any;
  const itemsCount = React.Children.count(truthyItems);
  const lastCls = `${componentCls}-item-last`;
  const items = React.Children.map(truthyItems, (ele: React.ReactElement<any>, idx) => {
    const pendingClass = idx === itemsCount - 2 ? lastCls : '';
    const readyClass = idx === itemsCount - 1 ? lastCls : '';
    return React.cloneElement(ele, {
      className: classNames([
        ele.props.className,
        !reverse && !!pending ? pendingClass : readyClass,
        getPositionCls(ele, idx),
      ]),
    });
  });

  const hasLabelItem = timeLineItems.some((item: any) => !!item?.props?.label);

  const mergeClassNames = classNames(componentCls, className, {
    [`${componentCls}-pending`]: !!pending,
    [`${componentCls}-reverse`]: !!reverse,
    [`${componentCls}-${mode}`]: !!mode && !hasLabelItem,
    [`${componentCls}-label`]: hasLabelItem,
  });

  return (
    <ul {...restProps} className={mergeClassNames}>
      {items}
    </ul>
  );
};

Timeline.defaultProps = {
  prefixCls: 'cobalt',
};

Timeline.Item = TimelineItem;

export default Timeline;
